import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../designSystem/Button';
import {
  PageHeaderIconType,
  PageHeaderIconVariant,
  PageHeaderTitleOnly,
} from '../../designSystem/PageHeader';
import { errorHandler } from '../../functions/errors';
import { showAlert } from '../../functions/utils';

interface DeleteItemResponse {
  displayMessage?: string;
}

type ActionType = 'delete' | 'reverse';
type WarningDialogMode = 'action' | 'info';

type AlertSeverity = 'error' | 'info' | 'success' | 'warning';

const SEVERITY_PAGE_HEADER: Record<
  AlertSeverity,
  { iconType: PageHeaderIconType; iconVariant: PageHeaderIconVariant }
> = {
  success: { iconType: 'success', iconVariant: 'success' },
  info: { iconType: 'info', iconVariant: 'info' },
  warning: { iconType: 'warning', iconVariant: 'warning' },
  error: { iconType: 'error', iconVariant: 'error' },
};

interface WarningDeleteDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  subtitle?: string;
  warningMessages?: string[];
  deleteItem: (dispatch: Dispatch) => Promise<DeleteItemResponse | void>;
  onDeleteSuccess?: () => void;
  confirmationText?: string;
  requireConfirmation?: boolean;
  children?: React.ReactNode;
  successMessage?: string;
  icon?: React.ReactNode;
  actionType?: ActionType;
  mode?: WarningDialogMode;
  severity?: 'error' | 'info' | 'success' | 'warning';
  previewLoading?: boolean;
  allowDelete?: boolean;
}

const WarningDeleteDialog: React.FC<WarningDeleteDialogProps> = ({
  open,
  setOpen,
  title,
  subtitle = 'Esta acción no se puede deshacer',
  warningMessages = [],
  deleteItem,
  onDeleteSuccess,
  confirmationText = 'Confirmo que entiendo las consecuencias de esta acción',
  requireConfirmation = true,
  children,
  successMessage,
  icon,
  actionType = 'delete',
  mode = 'action',
  severity,
  previewLoading = false,
  allowDelete = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const dispatch = useDispatch();

  const isInfoMode = mode === 'info';

  useEffect(() => {
    if (open) {
      setAgreeTerms(false);
    }
  }, [open]);

  const getActionConfig = () => {
    switch (actionType) {
      case 'reverse':
        return {
          buttonText: 'Reversar',
          buttonIcon: <UndoIcon />,
          defaultSuccessMessage: 'La reversión se ha completado exitosamente.',
          dependenciesText: 'Se revertirán todas las dependencias',
        };
      case 'delete':
      default:
        return {
          buttonText: 'Eliminar',
          buttonIcon: <DeleteIcon />,
          defaultSuccessMessage: 'El elemento ha sido eliminado exitosamente.',
          dependenciesText: 'Se eliminarán todas las dependencias',
        };
    }
  };

  const actionConfig = getActionConfig();

  const alertSeverity: AlertSeverity =
    severity || (isInfoMode ? 'info' : 'warning');

  const pageHeaderSeverity = SEVERITY_PAGE_HEADER[alertSeverity];

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteItem(dispatch);
      const message =
        response?.displayMessage ||
        successMessage ||
        actionConfig.defaultSuccessMessage;
      showAlert(dispatch, 'success', message);
      setOpen(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (value: boolean) => {
    if (!loading) {
      setOpen(value);
      if (!value) {
        setAgreeTerms(false);
      }
    }
  };

  const confirmationOk = !requireConfirmation || agreeTerms;
  const canSubmitDelete = confirmationOk && allowDelete && !previewLoading;

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleDialogClose}>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={0}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <PageHeaderTitleOnly
                variant="dialogHeader"
                {...(icon != null
                  ? {
                      icon,
                      iconVariant: 'outlined' satisfies PageHeaderIconVariant,
                    }
                  : {
                      iconType: pageHeaderSeverity.iconType,
                      iconVariant: pageHeaderSeverity.iconVariant,
                    })}
                title={title}
                subtitle={subtitle}
              />
            </Stack>
            <IconButton
              aria-label="close"
              onClick={() => handleDialogClose(false)}
              size="small"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {previewLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            warningMessages.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: isInfoMode ? 'info.lighter' : 'warning.lighter',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isInfoMode ? 'info.light' : 'warning.light',
                }}
              >
                <Stack spacing={0.5}>
                  {warningMessages.map((message, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      color="text.secondary"
                      component="div"
                    >
                      - {message}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )
          )}

          {children}

          {requireConfirmation && !isInfoMode && !previewLoading && (
            <Box
              sx={{
                mt: warningMessages.length > 0 ? 2 : 0,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setAgreeTerms(!agreeTerms)}
                    checked={agreeTerms}
                    sx={{ py: 0 }}
                  />
                }
                label={
                  <Typography variant="body2">{confirmationText}</Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      {!isInfoMode && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            onClick={handleDelete}
            color="error"
            disabled={!canSubmitDelete || loading}
            loading={loading}
            startIcon={actionConfig.buttonIcon}
          >
            {actionConfig.buttonText}
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default WarningDeleteDialog;
